/**
 * Pure MSAL configuration reader for the dashboard SPA (Concern 1 — Entra SSO).
 *
 * `readMsalConfig` is a side-effect-free function: given an environment map and
 * the current origin, it resolves the Entra MSAL configuration or reports which
 * required variables are absent/empty. It performs no I/O and reads no globals
 * (the caller passes `import.meta.env` and `window.location.origin`), which
 * keeps it fully property-testable (design Properties 6 and 7).
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 2.6.
 */

/**
 * Resolved Entra MSAL configuration consumed by `EntraAuthProvider`.
 * Sourced from Vite environment variables (Req 2.1-2.5).
 */
export interface MsalConfig {
  /** Entra tenant identifier — `VITE_TENANT_ID` (Req 2.1). */
  tenantId: string;
  /** Entra client identifier — `VITE_CLIENT_ID` (Req 2.2). */
  clientId: string;
  /** API scope — `VITE_API_SCOPE` (Req 2.3). */
  apiScope: string;
  /** Redirect URI — `VITE_REDIRECT_URI` when set and non-empty, else the
   *  current origin (Req 2.4, 2.5). */
  redirectUri: string;
}

/**
 * Outcome of reading the MSAL configuration. Either a fully resolved config, or
 * a failure listing exactly the required variables that were absent or empty so
 * the app can render the configuration-error state and withhold data (Req 2.6).
 */
export type ConfigResult =
  | { ok: true; config: MsalConfig }
  | { ok: false; missing: string[] };

/**
 * The required Entra configuration variables (Req 2.6). Order is preserved in
 * the returned `missing` list.
 */
const REQUIRED_VARS = [
  "VITE_TENANT_ID",
  "VITE_CLIENT_ID",
  "VITE_API_SCOPE",
] as const;

/** A value is "present and non-empty" when it is a string of length > 0. */
function isPresent(value: string | undefined): value is string {
  return typeof value === "string" && value.length > 0;
}

/**
 * Read and validate the MSAL configuration from an environment map.
 *
 * - Reads `VITE_TENANT_ID`, `VITE_CLIENT_ID`, `VITE_API_SCOPE` (Req 2.1-2.3).
 * - Resolves `redirectUri` to `VITE_REDIRECT_URI` when present and non-empty,
 *   otherwise to `origin` (Req 2.4, 2.5).
 * - Returns `{ ok: false, missing }` listing exactly the required variables
 *   that are absent or empty (Req 2.6); otherwise `{ ok: true, config }`.
 *
 * Pure: no I/O, no global access, deterministic in its inputs.
 *
 * @param env    Environment map (e.g. `import.meta.env`).
 * @param origin Current origin used as the redirect-URI fallback.
 */
export function readMsalConfig(
  env: Record<string, string | undefined>,
  origin: string,
): ConfigResult {
  const missing = REQUIRED_VARS.filter((name) => !isPresent(env[name]));

  if (missing.length > 0) {
    return { ok: false, missing };
  }

  const redirectEnv = env.VITE_REDIRECT_URI;
  const redirectUri = isPresent(redirectEnv) ? redirectEnv : origin;

  return {
    ok: true,
    config: {
      tenantId: env.VITE_TENANT_ID as string,
      clientId: env.VITE_CLIENT_ID as string,
      apiScope: env.VITE_API_SCOPE as string,
      redirectUri,
    },
  };
}
